<script lang="ts">
  import { createEventDispatcher } from "svelte"
  import { browser } from "$app/environment"
  import ActionDetails from "./ActionDetails.svelte"
  import ActionLogin from "./ActionLogin.svelte"
  import ActionSocialLogin from "./ActionSocialLogin.svelte"
  import ActionVerification from "./ActionVerification.svelte"
  import * as Tooltip from "$lib/components/ui/tooltip"
  import { Button } from "$lib/components/ui/button"
  import { Check, TriangleAlert, ChevronDown } from "lucide-svelte"
  import ActionIcon from "$lib/components/competition-creation/components/ActionIcon.svelte"
  import { findSetting } from "$lib/components/competition-creation/StepThree/actionData"
  import userStore, { userStoreActions } from "$lib/stores/userStore"
  import { getActionName } from "$lib/components/Widget2/utils"
  import { t } from "$lib/utils/i18n"

  interface Props {
    action: {
      id: string
      actions: {
        name: string
        verification?: Array<{
          name: string
          value?: string | number
        }>
      }
      requirements?: Array<{
        name: string
        value: number | string
      }>
    }
    competition: {
      id: string
      slug: string
      language?: string
      informationCollection?: {
        nameOption?: string
        phoneOption?: {
          collectPhone: boolean
        }
      }
      fraudSettings?: {
        enableCaptcha?: boolean
      }
      widgetCustomizations?: {
        colorSettings?: {
          entryBackgroundColor?: string
          primaryTextColor?: string
          actionTextColor?: string
          buttonColor?: string
          buttonTextColor?: string
          iconColor?: string
          iconBgColor?: string
          iconBorderRadius?: string
          entryActionBorderRadius?: string
          entryButtonBorderRadius?: string
        }
      }
    }
    isOpen?: boolean
    embed: boolean
    isLoading: boolean
    openTermsDialog: () => void
  }

  let {
    action,
    competition,
    isOpen = $bindable(false),
    embed,
    isLoading,
    openTermsDialog,
  }: Props = $props()

  const language = competition?.language || "en"

  // RTL language detection
  const rtlLanguages = ["ar", "he", "fa", "ur"]
  const isRTL = $derived(rtlLanguages.includes(language))

  let showLogin = $state(false)
  let isTooltipOpen = $state(false)
  let isMobile = $derived.by(() => {
    if (browser) {
      return window.matchMedia("(max-width: 768px)").matches
    }
    return false
  })

  const dispatch = createEventDispatcher()

  const showMoreDetails = (event: MouseEvent) => {
    event.stopPropagation()

    // TEMP CHANGE:
    if (competition.slug !== "papamoll_2025") {
      const actionCompleted = $userStore.user?.completedActionIds?.includes(
        action.id,
      )
      const verificationRequired =
        $userStore.actionsNeedingVerificationIds.includes(action.id)

      // If action is completed and doesn't require verification,
      // means its completed so we dont open the action
      if (actionCompleted && !verificationRequired) {
        return
      }
    }

    // If clicking on the currently open action, close it
    if ($userStore.openActionId === action.id) {
      userStoreActions.setOpenActionId(null)
      showLogin = false
      return
    }

    // If this action is blurred (another action is open), close the open action
    if (
      $userStore.openActionId !== null &&
      $userStore.openActionId !== action.id
    ) {
      userStoreActions.setOpenActionId(null)
      showLogin = false
      shouldBeOpen = false
      // Don't return here - we want to continue and open the clicked action
    }

    dispatch("actionClick", { actionId: action.id })

    // If user is not logged in, toggle login modal
    if ($userStore.user) {
      showLogin = false
    } else {
      showLogin = true
      return
    }

    // Set the action as open in the store
    userStoreActions.setOpenActionId(action.id)
  }

  function handleTooltipInteraction(event: MouseEvent) {
    if (isMobile) {
      event.preventDefault()
      // toggleTooltip()
    }
  }

  let lockUntilRequirement = $derived(findSetting(action, "Lock until"))
  let completedActions = $derived($userStore.user?.completedActionIds || [])
  let isLocked = $derived(
    lockUntilRequirement?.value > 0 &&
      completedActions.length < Number(lockUntilRequirement.value),
  )

  let ent = $derived(
    action.requirements?.find((r) => r.name === "Entries")?.value ?? 0,
  )
  let entryPluralized = $derived(
    t("widget_action_entries", {
      count: Number(ent),
    }),
  )
  let isInviteFriendsAction = $derived(action.actions.name === "Invite Friends")
  let needsVerification = $derived(() => {
    const isCompleted = $userStore?.user?.completedActionIds?.includes(
      action.id,
    )
    if (
      isCompleted &&
      $userStore.actionsNeedingVerificationIds.includes(action.id)
    ) {
      return true
    }
    return false
  })
  let actionIsCompleted = $derived((action: Props["action"]) => {
    const isCompleted = $userStore?.user?.completedActionIds?.includes(
      action.id,
    )
    return isCompleted
  })

  let shouldBeOpen = $derived(isOpen || $userStore.openActionId === action.id)
</script>

{#if isLoading}
  <div class="animate-pulse">
    <div
      class="flex items-center p-4 py-2 bg-gray-100 rounded-lg w-full justify-between"
      class:flex-row-reverse={isRTL}
    >
      <div class="flex items-center space-x-3" class:space-x-reverse={isRTL}>
        <!-- Icon skeleton -->
        <div class="w-6 h-6 bg-gray-200 rounded"></div>
        <!-- Text skeleton -->
        <div class="h-4 bg-gray-200 rounded w-32"></div>
      </div>
      <!-- Entry points skeleton -->
      <div class="w-12 h-8 bg-gray-200 rounded"></div>
    </div>
  </div>
{:else}
  <div
    class="action-container bg-white rounded-lg"
    class:border-2={shouldBeOpen}
    class:border-gray-500={shouldBeOpen}
    class:rounded-sm={shouldBeOpen}
    class:blur-sm={$userStore.openActionId !== null &&
      $userStore.openActionId !== action.id}
  >
    <button
      class="flex items-center py-2 bg-gray-100 w-full justify-between"
      class:px-4={!isRTL}
      class:pl-4={isRTL}
      class:pr-2={isRTL}
      style="background-color: {competition?.widgetCustomizations?.colorSettings
        ?.entryBackgroundColor}; border-radius: {competition
        ?.widgetCustomizations?.colorSettings?.entryActionBorderRadius ||
        '8px'};"
      class:hover:bg-gray-200={!actionIsCompleted(action) &&
        !isInviteFriendsAction &&
        !isMobile}
      class:active:bg-gray-200={!actionIsCompleted(action) &&
        !isInviteFriendsAction}
      class:rounded-b-none={shouldBeOpen}
      onclick={(event) => {
        if (competition.slug !== "papamoll_2025") {
          if (isLocked) return
        }

        // Always call showMoreDetails to handle the action click
        showMoreDetails(event)
      }}
    >
      <div class="flex items-center space-x-2" class:space-x-reverse={isRTL}>
        <ActionIcon action={action.actions} {isRTL} />
        <span
          class="leading-6 text-left"
          class:text-right={isRTL}
          class:text-left={!isRTL}
          style="color: {competition?.widgetCustomizations?.colorSettings
            ?.actionTextColor};">{@html getActionName(action, language)}</span
        >
      </div>
      {#if isLocked}
        <Tooltip.Provider delayDuration={0}>
          <Tooltip.Root bind:open={isTooltipOpen}>
            <Tooltip.Trigger>
              <Button
                class="shadow-none w-[56px]"
                style="background-color: {competition?.widgetCustomizations
                  ?.colorSettings?.buttonColor}; color: {competition
                  ?.widgetCustomizations?.colorSettings
                  ?.buttonTextColor}; border-radius: {competition
                  ?.widgetCustomizations?.colorSettings
                  ?.entryButtonBorderRadius || '6px'};">🔒 +{ent}</Button
              >
            </Tooltip.Trigger>
            <Tooltip.Content
              side={isRTL ? "right" : "left"}
              class={isRTL
                ? "-translate-x-10 sm:translate-x-0"
                : "translate-x-10 sm:translate-x-0"}
            >
              <p>
                {t("widget_action_locked", {
                  count: lockUntilRequirement.value,
                })}
              </p>
            </Tooltip.Content>
          </Tooltip.Root>
        </Tooltip.Provider>
      {:else if actionIsCompleted(action) && !isInviteFriendsAction}
        <Tooltip.Provider delayDuration={0}>
          <Tooltip.Root bind:open={isTooltipOpen}>
            <Tooltip.Trigger>
              <Button
                class="shadow-none bg-none bg-transparent hover:bg-transparent cursor-default {isRTL
                  ? '-ml-2'
                  : '-mr-2'}"
                onclick={handleTooltipInteraction}
                on:mouseenter={() => !isMobile && (isTooltipOpen = true)}
                on:mouseleave={() => !isMobile && (isTooltipOpen = false)}
              >
                {#if needsVerification()}
                  <div class="bg-yellow-500 rounded-full p-2">
                    <TriangleAlert size={16} color="white" />
                  </div>
                {:else}
                  <div class="bg-green-500 rounded-full p-2">
                    <Check size={16} color="white" />
                  </div>
                {/if}
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content
              side={isRTL ? "right" : "left"}
              class={isRTL
                ? "-translate-x-10 sm:translate-x-0"
                : "translate-x-10 sm:translate-x-0"}
            >
              <p>
                {t("widget_action_completed", {
                  count: ent,
                  entries: entryPluralized,
                })}
              </p>
            </Tooltip.Content>
          </Tooltip.Root>
        </Tooltip.Provider>
      {:else}
        <Tooltip.Provider delayDuration={0}>
          <Tooltip.Root>
            <Tooltip.Trigger>
              <Button
                class="action-header-open shadow-none w-[50px]"
                style="background-color: {competition?.widgetCustomizations
                  ?.colorSettings?.buttonColor}; color: {competition
                  ?.widgetCustomizations?.colorSettings
                  ?.buttonTextColor}; border-radius: {competition
                  ?.widgetCustomizations?.colorSettings
                  ?.entryButtonBorderRadius || '6px'};"
                onclick={handleTooltipInteraction}
                on:mouseenter={() => !isMobile && (isTooltipOpen = true)}
                on:mouseleave={() => !isMobile && (isTooltipOpen = false)}
              >
                {#if shouldBeOpen}
                  <ChevronDown />
                {:else}
                  +{ent}
                {/if}
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content side={isRTL ? "right" : "left"}>
              <p>
                {t("widget_action_worth", {
                  count: ent,
                  entries: entryPluralized,
                })}
              </p>
            </Tooltip.Content>
          </Tooltip.Root>
        </Tooltip.Provider>
      {/if}
    </button>
    {#if shouldBeOpen}
      <div class="action-content">
        {#if !$userStore.user && showLogin}
          {#if competition.informationCollection?.enableSocialLogin && competition.informationCollection?.socialLoginProviders?.length > 0}
            <ActionSocialLogin
              {competition}
              {embed}
              {action}
              {openTermsDialog}
              infoCollection={competition.informationCollection}
              fraudSettings={competition.fraudSettings}
              isSubmitting={false}
              on:close={() => {
                showLogin = false
              }}
            />

            <div class="flex items-center my-4">
              <div class="flex-1 border-t border-gray-300"></div>
              <span class="px-3 text-sm text-gray-500">OR</span>
              <div class="flex-1 border-t border-gray-300"></div>
            </div>
          {/if}

          <!-- Email Login Section -->
          <ActionLogin
            {competition}
            {embed}
            {action}
            {openTermsDialog}
            infoCollection={competition.informationCollection}
            fraudSettings={competition.fraudSettings}
            isSubmitting={false}
            on:close={() => {
              showLogin = false
            }}
          />
        {:else if needsVerification() && $userStore.openActionId === action.id}
          <ActionVerification {action} {competition} />
        {:else if $userStore.openActionId === action.id}
          <ActionDetails {action} {competition} {embed} />
        {/if}
      </div>
    {/if}
  </div>
{/if}

<style>
  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
</style>
